// React

// Packages

// Utilities
import { useInitiativeDataStore } from 'utilities/store';

// Hooks
import { useLabels } from 'utilities/hooks';

// ItemBuilder Contents
import ActivityCBCEngagement from 'components/_itemBuilderContents/activityCBCEngagement';
import ActivityCBCService from 'components/_itemBuilderContents/activityCBCService';
import ActivityCBCTeaching from 'components/_itemBuilderContents/activityCBCTeaching';
import ActivityDevelopment from 'components/_itemBuilderContents/activityDevelopment';
import ActivityDevelopmentPep from 'components/_itemBuilderContents/activityDevelopmentPep';
import ActivityEngagement from 'components/_itemBuilderContents/activityEngagement';
import ActivityEOTeaching from 'components/_itemBuilderContents/activityEOTeaching';
import ActivityIntervention from 'components/_itemBuilderContents/activityIntervention';
import ActivityInterventionBasic from 'components/_itemBuilderContents/activityInterventionBasic';
import ActivityPhysical from 'components/_itemBuilderContents/activityPhysical';
import ActivityService from 'components/_itemBuilderContents/activityService';
import ActivityTeaching from 'components/_itemBuilderContents/activityTeaching';
import ActivityTeamEducation from 'components/_itemBuilderContents/activityTeamEducation';
import ActivityUpgrade from 'components/_itemBuilderContents/activityUpgrade';

// Card Contents
import ActivityDevelopmentCard from 'components/_cardContents/activityDevelopment';
import ActivityEngagementCard from 'components/_cardContents/activityEngagement';
import ActivityInterventionCard from 'components/_cardContents/activityIntervention';
import ActivityPhysicalCard from 'components/_cardContents/activityPhysical';
import ActivityServiceCard from 'components/_cardContents/activityService';
import ActivityTeachingCard from 'components/_cardContents/activityTeaching';
import ActivityTeamEducationCard from 'components/_cardContents/activityTeamEducation';
import ActivityUpgradeCard from 'components/_cardContents/activityUpgrade';

// Legacy card contents
import ActivityDisseminationCard from 'components/_cardContents/activityDissemination';
import ActivityEvaluationCard from 'components/_cardContents/activityEvaluation';

const useActivityHelpers = () => {
    // ///////////////////
    // STORES
    // ///////////////////

    const { CONSTANTS, utilities } = useInitiativeDataStore();

    // ///////////////////
    // HOOKS
    // ///////////////////

    const { getValueLabel } = useLabels();

    // ///////////////////
    // DATA
    // ///////////////////

    const itemBuilderMap = {
        [CONSTANTS.ACTIVITIES.CBC_ENGAGEMENT]: ActivityCBCEngagement,
        [CONSTANTS.ACTIVITIES.CBC_SERVICE]: ActivityCBCService,
        [CONSTANTS.ACTIVITIES.CBC_TEACHING]: ActivityCBCTeaching,
        [CONSTANTS.ACTIVITIES.DEVELOPMENT]: ActivityDevelopment,
        [CONSTANTS.ACTIVITIES.DEVELOPMENT_PEP]: ActivityDevelopmentPep,
        [CONSTANTS.ACTIVITIES.ENGAGEMENT]: ActivityEngagement,
        [CONSTANTS.ACTIVITIES.EO_TEACHING]: ActivityEOTeaching,
        [CONSTANTS.ACTIVITIES.INTERVENTION]: {
            basic: ActivityInterventionBasic,
            default: ActivityIntervention,
        },
        [CONSTANTS.ACTIVITIES.PHYSICAL]: ActivityPhysical,
        [CONSTANTS.ACTIVITIES.SERVICE]: ActivityService,
        [CONSTANTS.ACTIVITIES.TEACHING]: ActivityTeaching,
        [CONSTANTS.ACTIVITIES.TEACHING_CAPACITY]: ActivityTeaching,
        [CONSTANTS.ACTIVITIES.TEAM_EDUCATION]: ActivityTeamEducation,
        [CONSTANTS.ACTIVITIES.UPGRADE]: ActivityUpgrade,
        // Legacy
        [CONSTANTS.ACTIVITIES.DISSEMINATION]: null,
        [CONSTANTS.ACTIVITIES.EVALUATION]: null,
    };

    const cardMap = {
        [CONSTANTS.ACTIVITIES.CBC_ENGAGEMENT]: ActivityEngagementCard,
        [CONSTANTS.ACTIVITIES.CBC_SERVICE]: ActivityServiceCard,
        [CONSTANTS.ACTIVITIES.CBC_TEACHING]: ActivityTeachingCard,
        [CONSTANTS.ACTIVITIES.DEVELOPMENT]: ActivityDevelopmentCard,
        [CONSTANTS.ACTIVITIES.DEVELOPMENT_PEP]: ActivityDevelopmentCard,
        [CONSTANTS.ACTIVITIES.ENGAGEMENT]: ActivityEngagementCard,
        [CONSTANTS.ACTIVITIES.EO_TEACHING]: ActivityTeachingCard,
        [CONSTANTS.ACTIVITIES.INTERVENTION]: ActivityInterventionCard,
        [CONSTANTS.ACTIVITIES.PHYSICAL]: ActivityPhysicalCard,
        [CONSTANTS.ACTIVITIES.SERVICE]: ActivityServiceCard,
        [CONSTANTS.ACTIVITIES.TEACHING]: ActivityTeachingCard,
        [CONSTANTS.ACTIVITIES.TEAM_EDUCATION]: ActivityTeamEducationCard,
        [CONSTANTS.ACTIVITIES.UPGRADE]: ActivityUpgradeCard,
        // Legacy
        [CONSTANTS.ACTIVITIES.DISSEMINATION]: ActivityDisseminationCard,
        [CONSTANTS.ACTIVITIES.EVALUATION]: ActivityEvaluationCard,
    };

    const subTypeMap = {
        [CONSTANTS.ACTIVITIES.CBC_ENGAGEMENT]: 'CBC_Engagement_Type__c',
        [CONSTANTS.ACTIVITIES.CBC_SERVICE]: 'CBC_Delivery_Method__c',
        [CONSTANTS.ACTIVITIES.CBC_TEACHING]: 'CBC_Teaching_Type__c',
        [CONSTANTS.ACTIVITIES.DEVELOPMENT]: 'Material_Type__c',
        [CONSTANTS.ACTIVITIES.DEVELOPMENT_PEP]: 'Material_Type__c',
        [CONSTANTS.ACTIVITIES.ENGAGEMENT]: 'Engagement_Method__c',
        [CONSTANTS.ACTIVITIES.EO_TEACHING]: 'EO_Teaching_Type__c',
        [CONSTANTS.ACTIVITIES.INTERVENTION]: null,
        [CONSTANTS.ACTIVITIES.PHYSICAL]: 'Implementation_Stage__c',
        [CONSTANTS.ACTIVITIES.SERVICE]: 'Delivery_Method__c',
        [CONSTANTS.ACTIVITIES.TEACHING]: 'Teaching_Type__c',
        [CONSTANTS.ACTIVITIES.TEACHING_CAPACITY]: 'Teaching_Type__c',
        [CONSTANTS.ACTIVITIES.TEAM_EDUCATION]: 'Education_Type__c',
        [CONSTANTS.ACTIVITIES.UPGRADE]: null,
        // Legacy
        [CONSTANTS.ACTIVITIES.DISSEMINATION]: 'Dissemination_Type__c',
        [CONSTANTS.ACTIVITIES.EVALUATION]: 'Evaluation_Type__c',
    };

    // ///////////////////
    // METHODS
    // ///////////////////

    function getItemBuilderComponent(activityId) {
        const activity = utilities.activities.get(activityId);
        if (!activity) return {};

        return getItemBuilderComponentFromType(activity.Activity_Type__c);
    }

    function getItemBuilderComponentFromType(activityType) {
        // Need to check for intervention type based on initiative configuration type
        if (activityType === CONSTANTS.ACTIVITIES.INTERVENTION) {
            return utilities.initiative.get().Configuration_Type__c ===
                CONSTANTS.INITIATIVE.BASIC
                ? itemBuilderMap[CONSTANTS.ACTIVITIES.INTERVENTION].basic
                : itemBuilderMap[CONSTANTS.ACTIVITIES.INTERVENTION].default;
        }

        return itemBuilderMap[activityType];
    }

    function getCardComponent(activityId) {
        const activity = utilities.activities.get(activityId);
        if (!activity) return null;

        return cardMap[activity.Activity_Type__c] ?? null;
    }

    function getTypeLabels(activityId) {
        const activity = utilities.activities.get(activityId);
        if (!activity) return {};

        return {
            type: getValueLabel(
                'Initiative_Activity__c.Activity_Type__c',
                activity.Activity_Type__c
            ),
            subType: subTypeMap[activity.Activity_Type__c]
                ? getValueLabel(
                      `Initiative_Activity__c.${
                          subTypeMap[activity.Activity_Type__c]
                      }`,
                      activity[subTypeMap[activity.Activity_Type__c]]
                  )
                : null,
        };
    }

    // ///////////////////
    // RETURN
    // ///////////////////

    return {
        getItemBuilderComponent,
        getItemBuilderComponentFromType,
        getCardComponent,
        getTypeLabels,
    };
};

export default useActivityHelpers;
